const COLORS = { background: 'rgba(13, 35, 38, .9)', border: 'rgba(236, 214, 164, .5)', text: '#fff3d4', muted: '#d6e0cd', key: '#efd19a', keyText: '#183b3d' };
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const overlaps = (a, b, gap = 0) => a.x < b.x + b.width + gap && a.x + a.width > b.x - gap && a.y < b.y + b.height + gap && a.y + a.height > b.y - gap;
const validRect = rect => rect && [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite) && rect.width > 0 && rect.height > 0;

function wrapText(text, width, measure, maxLines) {
  const lines = []; let current = '';
  for (const character of Array.from(String(text))) {
    if (character === '\n') { lines.push(current); current = ''; continue; }
    if (current && measure(current + character) > width) { lines.push(current); current = ''; }
    current += character;
  }
  if (current) lines.push(current);
  if (lines.length > maxLines) {
    lines.length = maxLines;
    let last = Array.from(lines[maxLines - 1]);
    while (last.length && measure(last.join('') + '…') > width) last.pop();
    lines[maxLines - 1] = last.join('') + '…';
  }
  return lines;
}

/**
 * All input and output positions are CSS pixels, including measured text widths.
 * measureText receives (text, font), and may return a number or TextMetrics.
 * An off-screen anchor or fully occupied scene returns null instead of covering UI.
 */
export function layoutCharacterTip({ screen, anchor, text = '', actionLabel = '', keyLabel,
  measureText, avatarRect, statusRect, navRect, panelRect, controlsRect, obstacles = [], maxLines = 3,
  maxWidth = 360, pixelRatio = 1 } = {}) {
  if (!screen || !anchor || !Number.isFinite(anchor.x) || !Number.isFinite(anchor.y)) return null;
  const width = Number(screen.width), height = Number(screen.height);
  if (!(width > 0 && height > 0) || (!String(text).trim() && !String(actionLabel).trim())) return null;
  const safe = screen.safeArea || {};
  const bounds = { x: Math.max(0, Number(safe.left) || 0) + 8, y: Math.max(0, Number(safe.top) || 0) + 8,
    right: width - Math.max(0, Number(safe.right) || 0) - 8, bottom: height - Math.max(0, Number(safe.bottom) || 0) - 8 };
  if (anchor.x < 0 || anchor.x > width || anchor.y < 0 || anchor.y > height) return null;
  const compact = width < 600 || height < 500;
  const ratio = Math.max(1, Math.min(3, Number(pixelRatio) || 1));
  const fontSize = compact ? 14 : 16;
  const font = `500 ${fontSize}px sans-serif`, keyFont = `600 ${compact ? 12 : 13}px sans-serif`;
  const lineHeight = compact ? 20 : 23, padding = 12, gap = 10;
  const measure = (value, chosenFont = font) => {
    const result = measureText?.(value, chosenFont);
    const measured = typeof result === 'number' ? result : result?.width;
    return Number.isFinite(measured) ? measured : Array.from(value).reduce((sum, char) => sum + (/^[\x00-\x7f]$/.test(char) ? fontSize * .6 : fontSize), 0);
  };
  const badge = actionLabel ? String(keyLabel ?? (screen.isTouch ? '点按' : 'E')) : '';
  const keyWidth = badge ? Math.max(22, measure(badge, keyFont) + 14) : 0;
  const availableWidth = Math.floor(Math.min(maxWidth, compact ? 320 : 360, bounds.right - bounds.x) * ratio) / ratio;
  const contentWidth = availableWidth - padding * 2;
  if (contentWidth < fontSize * 3 || (badge && contentWidth < keyWidth + gap + fontSize)) return null;
  const lines = wrapText(text, contentWidth, value => measure(value), Math.max(1, Math.floor(maxLines)));
  const actionLines = actionLabel ? wrapText(actionLabel, contentWidth - keyWidth - gap, value => measure(value), 2) : [];
  const measuredWidth = Math.max(0, ...lines.map(value => measure(value)),
    ...actionLines.map(value => keyWidth + gap + measure(value)));
  const tipWidth = Math.min(availableWidth, Math.ceil(Math.max(84, measuredWidth + padding * 2) * ratio) / ratio);
  const actionGap = lines.length && actionLines.length ? 7 : 0;
  const tipHeight = Math.ceil((padding * 2 + (lines.length + actionLines.length) * lineHeight + actionGap) * ratio) / ratio;
  if (tipHeight > bounds.bottom - bounds.y) return null;
  const actor = validRect(avatarRect) ? avatarRect : { x: anchor.x - 18, y: anchor.y + 4, width: 36, height: 48 };
  const blocked = [actor, statusRect, navRect, panelRect, controlsRect, ...obstacles].filter(validRect);
  const above = { x: anchor.x - tipWidth / 2, y: actor.y - tipHeight - gap };
  const positions = [above,
    { x: actor.x - tipWidth - gap, y: actor.y - tipHeight / 2 },
    { x: actor.x + actor.width + gap, y: actor.y - tipHeight / 2 },
    { x: anchor.x - tipWidth / 2, y: actor.y + actor.height + gap }];
  // Obstacle edges are additional candidate locations, avoiding blind global corners.
  for (const rect of blocked) {
    positions.push({ x: rect.x - tipWidth - gap, y: above.y },
      { x: rect.x + rect.width + gap, y: above.y },
      { x: above.x, y: rect.y - tipHeight - gap },
      { x: above.x, y: rect.y + rect.height + gap });
  }
  let best = null, bestDistance = Infinity;
  for (const position of positions) {
    const rect = { x: clamp(position.x, bounds.x, bounds.right - tipWidth), y: clamp(position.y, bounds.y, bounds.bottom - tipHeight), width: tipWidth, height: tipHeight };
    if (blocked.some(block => overlaps(rect, block, 4))) continue;
    const dx = rect.x + tipWidth / 2 - anchor.x, dy = rect.y + tipHeight - actor.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) { best = rect; bestDistance = distance; }
  }
  if (!best) return null;
  // CSS dimensions are quantized to physical pixels, keeping the texture/display ratio exact.
  return { rect: best, lines, actionLines, keyLabel: badge, keyWidth, font, keyFont, fontSize, lineHeight,
    padding, gap, actionGap, radius: 10, pixelRatio: ratio,
    canvas: { width: Math.ceil(tipWidth * ratio), height: Math.ceil(tipHeight * ratio) },
    colors: COLORS };
}

function roundedRect(ctx, x, y, width, height, radius) {
  radius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath(); ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius); ctx.closePath();
}

/** Draw into a local texture canvas whose display size is layout.rect.width/height. */
export function drawCharacterTip(ctx, layout) {
  if (!layout) return false;
  const { rect, pixelRatio, padding, lineHeight, lines, actionLines, keyWidth, gap, colors } = layout;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, layout.canvas.width, layout.canvas.height);
  ctx.scale(pixelRatio, pixelRatio);
  roundedRect(ctx, .75, .75, rect.width - 1.5, rect.height - 1.5, layout.radius);
  ctx.fillStyle = colors.background; ctx.fill();
  ctx.lineWidth = 1; ctx.strokeStyle = colors.border; ctx.stroke();
  ctx.font = layout.font; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillStyle = colors.text;
  let y = padding + lineHeight / 2;
  for (const line of lines) { ctx.fillText(line, padding, y); y += lineHeight; }
  if (actionLines.length) {
    y += layout.actionGap;
    const badgeHeight = lineHeight;
    roundedRect(ctx, padding, y - badgeHeight / 2, keyWidth, badgeHeight, 5);
    ctx.fillStyle = colors.key; ctx.fill();
    ctx.font = layout.keyFont; ctx.fillStyle = colors.keyText; ctx.textAlign = 'center';
    ctx.fillText(layout.keyLabel, padding + keyWidth / 2, y);
    ctx.font = layout.font; ctx.fillStyle = colors.text; ctx.textAlign = 'left';
    for (const line of actionLines) { ctx.fillText(line, padding + keyWidth + gap, y); y += lineHeight; }
  }
  ctx.restore();
  return true;
}
